import { transformForForm, transformForSubmit } from '@components/cars-table-next/filter/lib';
import { useFilter } from '@components/cars-table-next/filter/useFilter';
import { isNil } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { useCustomerMode } from 'src/hooks/useCustomerMode';
import { useExtendUsersnap } from 'src/hooks/useExtendUsersnap';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import useRole from 'src/hooks/useRole';
import { useSearchAgent } from 'src/hooks/useSearchAgent';
import { UserPermissions, useUserPermissions } from 'src/hooks/useUserPermissions';
import { useValuationSettings } from 'src/hooks/useValuationSettings';
import { ValuationCountryCode } from 'src/modules/generated/api';
import { CarTableColumn } from 'src/modules/table-data';
import { getGlobalFilterAndCarDataGTM } from 'src/modules/tag-manager-helpers';
import { SortOn } from 'src/stores/CarsTable';
import { searchAgentState } from 'src/stores/state';
import { CarsFilterSettings } from 'src/types/CarsFilterSettings';
import UserRole from 'src/types/UserRoles';

import { columns, relevantTaxes } from './columns';
import { carsTablePageIndexState, carsTablePaginationState, refetchIntervalState, sortingState } from './state';
import { TableNext } from './TableNext';
import { TableType } from './types';
import { useCars } from './useCars';

export const CarsTableNext = (): JSX.Element => {
  const { hasRole } = useRole();
  const { valuationCountry, valuationType } = useValuationSettings();
  const packageViewFlagEnabled = useFeatureFlags('packageView');
  const hasPackageViewPermission = useUserPermissions(UserPermissions.PACKAGE_VIEW);
  const showPackageView = packageViewFlagEnabled && hasPackageViewPermission;

  const [searchAgentId, setSearchAgentId] = useRecoilState(searchAgentState);
  const { data: searchAgent } = useSearchAgent(searchAgentId);

  const { customerModeAvailable } = useCustomerMode();

  const availableColumns = useMemo(
    () =>
      columns()
        .filter(
          (column) =>
            column.meta?.roles === undefined ||
            hasRole(UserRole.ADMIN) ||
            column.meta.roles.every((role) => hasRole(role as any) || relevantTaxes[valuationCountry!]?.includes(role)),
        )
        .filter((column) => !column.meta?.isCustomerModeVisible || customerModeAvailable),
    [customerModeAvailable, hasRole, valuationCountry],
  );
  // if the users valuationCountry is De then dont display the columns "marketPriceDE" and "referenceVehiclesDE"
  // CZ columns (marketPriceCZ, referenceVehiclesCZ, potential.CZ.*) are only shown to SK users
  // SK columns (marketPriceSK, referenceVehiclesSK, potential.SK.*) are only shown to CZ users
  const filteredColumns = useMemo(
    () =>
      availableColumns.filter((column) => {
        if (
          valuationCountry === ValuationCountryCode.De &&
          (column.id === 'marketPriceDE' || column.id === 'referenceVehiclesDE')
        )
          return false;
        if (
          column.id === 'marketPriceCZ' ||
          column.id === 'referenceVehiclesCZ' ||
          column.id?.startsWith('potential.CZ.')
        )
          return valuationCountry === ValuationCountryCode.Sk;
        if (
          column.id === 'marketPriceSK' ||
          column.id === 'referenceVehiclesSK' ||
          column.id?.startsWith('potential.SK.')
        )
          return valuationCountry === ValuationCountryCode.Cz;
        return true;
      }),
    [availableColumns, valuationCountry],
  );

  const { reset: resetFilter, watch, setFilterSafe } = useFilter();
  const filterState = watch();
  const [pagination, setPagination] = useRecoilState(carsTablePaginationState);
  const [sorting] = useRecoilState(sortingState);
  const refetchInterval = useRecoilValue(refetchIntervalState);
  const resetPageIndexSate = useResetRecoilState(carsTablePageIndexState);

  const {
    data: carsList,
    // isFetching,
    isLoading,
    refetch,
    isPreviousData,
  } = useCars({
    enabled: !isNil(filterState),
    filter: filterState,
    pagination,
    sorting,
    refetchInterval: refetchInterval || undefined,
    showPackageView,
  });

  useExtendUsersnap(
    {
      transformedFilter: filterState && transformForSubmit(filterState),
      pagination,
      sorting,
      valuationCountry,
      valuationType,
    },
    [filterState, pagination, sorting, valuationCountry, valuationType],
  );

  // Track list-update event on initial load only
  const hasTrackedInitialLoad = useRef(false);
  useEffect(() => {
    if (!hasTrackedInitialLoad.current && !isLoading && carsList?.data?.length) {
      hasTrackedInitialLoad.current = true;
      const sortOn: SortOn = sorting[0]
        ? { column: sorting[0].id as SortOn['column'], dir: sorting[0].desc ? 'desc' : 'asc' }
        : { column: CarTableColumn.DateEnd, dir: 'asc' };
      TagManager.dataLayer(
        getGlobalFilterAndCarDataGTM(
          'list-update',
          carsList.data,
          pagination.pageIndex + 1,
          pagination.pageSize,
          sortOn,
          transformForSubmit(filterState) as unknown as CarsFilterSettings,
        ),
      );
    }
  }, [isLoading, carsList?.data, sorting, pagination.pageIndex, pagination.pageSize, filterState]);

  useEffect(() => {
    const subscription = watch(() => {
      resetPageIndexSate();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, resetPageIndexSate]);

  // set a filter state via Url, IRIS-4918
  useEffect(() => {
    if (searchAgentId && searchAgent?.id === searchAgentId) {
      setFilterSafe(transformForForm(searchAgent));
      setSearchAgentId(null);
    }
  }, [searchAgentId, searchAgent, setFilterSafe, setSearchAgentId]);

  // add rowId to display elements virtualized
  const carsListData = useMemo(
    () =>
      carsList?.data?.map((car) => {
        const packageId = car.packageAggregation?.packageId;
        return {
          ...car,
          rowId: packageId ? `package-${packageId}` : `car-${car.carId}`,
          isPackageBid: showPackageView && !!packageId,
        };
      }),
    [carsList?.data, showPackageView],
  );

  return (
    <TableNext
      tableColumns={filteredColumns}
      totalRecords={carsList?.recordsTotal}
      data={carsListData}
      isLoading={isLoading}
      refetch={refetch}
      isPreviousData={isPreviousData}
      resetFilter={resetFilter}
      tableType={TableType.Cars}
      pagination={pagination}
      setPagination={setPagination}
    />
  );
};
